import React, {useEffect} from 'react';
import {t} from 'ttag';
import {
  Box,
  Button, CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography
} from '@mui/material';
import {Close, SendOutlined} from '@mui/icons-material';
import {SendInvoiceForm, styles} from '@invoice-system';
import {IInvoice} from '../../../../store/apis/interfaces/invoice';
import {FieldValues, useForm, UseFormReturn} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import {useSendInvoiceMutation} from '../../../../store/apis/ccs';
import {useAccount} from '@azure/msal-react';
import {useSnackbar} from 'notistack';

interface SendInvoiceDialogProps {
  onClose: () => void;
  data: IInvoice
}

export const SendInvoiceDialog = ({onClose, data}: SendInvoiceDialogProps) => {
  const [mode, setMode] = React.useState<'resend' | 'new'>('resend');
  const defaultEmail = data?.email ?? '';
  const [sendEmail, { isLoading }] = useSendInvoiceMutation();
  const account = useAccount();
  const {enqueueSnackbar} = useSnackbar();

  const methods = useForm({
    defaultValues: {
      email: defaultEmail
    },
    resolver: yupResolver(
      Yup.object().shape({
        email: Yup.string()
          .nullable()
          .required(t`Required field.`)
          .matches(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/, t`This email is not valid.`)})
    )
  });

  const handleModeChange = (value: 'resend' | 'new') => {
    if (value !== mode) {
      setMode(value);
      methods.reset({email: value === 'resend' ? defaultEmail : ''});
    }
  };

  const handleSubmit = methods.handleSubmit((values) => {
    sendEmail({
      employeeId: account?.username?.split('@')[0] as string,
      email: values.email,
      ticket: data.ticket
    }).then((r: any) => {
      if (r?.error) {
        enqueueSnackbar(r?.error?.message, {variant: 'error'});
        return;
      }
      enqueueSnackbar(t`Invoice was sent successfully to` + ` ${values.email}`, {variant: 'success'});
      onClose();
    })
  }, (error) => console.error(error));

  useEffect(() => {
    if (methods.watch('email') !== defaultEmail) mode === 'resend' && setMode('new');
    else setMode('resend');
  }, [methods.watch('email'), data]);

  return (
    <Dialog open={true} maxWidth={'xs'} fullWidth onClose={isLoading ? undefined : onClose}>
      <DialogTitle>
        <Box display={'flex'} justifyContent={'space-between'} alignItems={'start'}>
          <Box display={'flex'} flexDirection={'column'}>
            <Typography sx={styles.subtitle}>{t`Send Invoice` + ` ${data?.docId}`}</Typography>
            <Typography
              variant={'body2'}
              color={'text.secondary'}
            >{t`Choose how you want to send this invoice.`}</Typography>
          </Box>
          <IconButton onClick={isLoading ? undefined : onClose} size={'small'}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <Divider />
      <DialogContent>
        <Box
          sx={{
            backgroundColor: 'rgba(0, 85, 138, 0.075)',
            display: 'flex',
            justifyContent: 'center',
            mb: 2,
            borderRadius: '4px',
            p: '.25rem'
          }}
        >
          <Button
            sx={styles.selectModeButton(mode === 'resend')}
            onClick={() => handleModeChange('resend')}
            size={'small'}
            fullWidth
            color={'secondary'}
          >
            {'Resend'}
          </Button>
          <Button
            sx={styles.selectModeButton(mode === 'new')}
            onClick={() => handleModeChange('new')}
            size={'small'}
            fullWidth
            color={'secondary'}
          >
            {'New Address'}
          </Button>
        </Box>
        <Typography variant={'body2'} color={'text.secondary'} mb={1.5}>
          {mode === 'resend'
            ? t`Resend the invoice to the original recipient.`
            : t`Send the invoice to a new recipient.`}
        </Typography>
        <SendInvoiceForm {...(methods as unknown as UseFormReturn<FieldValues>)} />
      </DialogContent>
      <Divider />
      <DialogActions sx={{px: 3, py: 1.5}}>
        <Button
          variant={'contained'}
          color={'secondary'}
          startIcon={isLoading ? <CircularProgress size={20}/> : <SendOutlined sx={{rotate: '-40deg', mb: 0.5}} />}
          fullWidth={true}
          onClick={handleSubmit}
          disabled={isLoading}
        >{t`Resend Invoice`}</Button>
      </DialogActions>
    </Dialog>
  );
};
